import { IdentitySource } from '@infiltre/shared';
import { type Api, createApi } from '@/shared/lib/api';
import type { Identity, IdentityAcquireRequest, IdentityProvider } from './types';

/**
 * Tauri 2 identity. Same auth flow as web; bearer in `tauri-plugin-store`
 * (`infiltre-session.json`). Sync `getToken` via in-memory cache.
 */
const STORE_FILE = 'infiltre-session.json';
const TOKEN_KEY = 'infiltre:token:v1';

interface TauriStore {
  get<T = unknown>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<boolean>;
  save(): Promise<void>;
}

let storePromise: Promise<TauriStore> | null = null;

const loadStore = async (): Promise<TauriStore> => {
  // Lazy import — plugin only resolves inside Tauri runtime.
  const mod = (await import('@tauri-apps/plugin-store')) as unknown as {
    Store: { load(path: string): Promise<TauriStore> };
  };
  return mod.Store.load(STORE_FILE);
};

const getStore = (): Promise<TauriStore> => {
  if (!storePromise) {
    storePromise = loadStore().catch((err: unknown) => {
      // Wipe so retries don't hit cached rejection.
      storePromise = null;
      const reason = err instanceof Error ? err.message : String(err);
      throw new Error(`identity_failed: tauri store unavailable (${reason})`);
    });
  }
  return storePromise;
};

const formatErr = (err: { value: unknown }): string => {
  const v = err.value;
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'error' in v && typeof v.error === 'string') return v.error;
  return JSON.stringify(v);
};

export const createTauriIdentityProvider = (apiBaseUrl: string): IdentityProvider => {
  let cachedToken: string | null = null;
  let api: Api | null = null;

  const getToken = (): string | null => cachedToken;

  const getApi = (): Api => {
    if (!api) api = createApi(apiBaseUrl, getToken);
    return api;
  };

  const persistToken = async (token: string): Promise<void> => {
    cachedToken = token;
    const store = await getStore();
    await store.set(TOKEN_KEY, token);
    await store.save();
  };

  const dropToken = async (): Promise<void> => {
    cachedToken = null;
    try {
      const store = await getStore();
      await store.delete(TOKEN_KEY);
      await store.save();
    } catch {
      // store unavailable — in-memory clear is sufficient
    }
  };

  const acquire = async (request?: IdentityAcquireRequest): Promise<Identity> => {
    const nickname = request?.nickname?.trim();
    if (!nickname) throw new Error('identity_failed: nickname required for anon login');
    const res = await getApi().auth.anon.post({ name: nickname });
    if (res.error) throw new Error(formatErr(res.error));
    const data = res.data as { token: string; playerId: string; name: string };
    await persistToken(data.token);
    return { playerId: data.playerId, name: data.name, source: IdentitySource.Anon };
  };

  const restore = async (): Promise<Identity | null> => {
    let stored: string | null;
    try {
      const store = await getStore();
      const value = await store.get<string>(TOKEN_KEY);
      stored = typeof value === 'string' ? value : null;
    } catch {
      return null;
    }
    if (!stored) return null;
    cachedToken = stored;
    try {
      const res = await getApi().auth.me.get();
      if (res.error) {
        await dropToken();
        return null;
      }
      const data = res.data as { playerId: string; name: string; source: IdentitySource } | null;
      if (!data) {
        await dropToken();
        return null;
      }
      return { playerId: data.playerId, name: data.name, source: data.source };
    } catch {
      await dropToken();
      return null;
    }
  };

  const clear = async (): Promise<void> => {
    await dropToken();
  };

  const setToken = (token: string): void => {
    cachedToken = token;
    void persistToken(token).catch(() => {
      // best-effort persist; cached token still works for the session
    });
  };

  return { acquire, restore, clear, getToken, setToken, source: IdentitySource.Anon };
};
